import { showNotification } from '@helpers/notification-helper';
import { useAppSelector } from '@hooks/app/redux';
import usePendingActionsRefresher from '@hooks/query/pendingActions/usePendingActionsRefresher';
import { responseToPairing } from '@services/external/client.api';
import { Modal } from '@ui';
import React from 'react';
import { IClientSearchItem, TClientPairingStatus } from 'types/client.types';

interface PairingModalProps {
  visible: boolean;
  mode?: TClientPairingStatus;
  client?: IClientSearchItem;

  onClose: () => void;
  afterSubmit: () => void;
}

const PairingModal: React.FunctionComponent<PairingModalProps> = ({
  visible,
  onClose,
  mode,
  afterSubmit,
  client,
}) => {
  const [loading, setLoading] = React.useState(false);
  const providerId = useAppSelector((state) => state.users.currentProvider?.id)!;
  const { refetch } = usePendingActionsRefresher();
  const title = mode === 'paired' ? 'Prijatie pacienta' : 'Odmietnutie pacienta';

  const handlePairing = React.useCallback(async () => {
    if (!client || !mode) {
      return;
    }

    setLoading(true);

    try {
      await responseToPairing(providerId, client.id, mode);
      refetch(['base', 'businessProfile']);
      afterSubmit();
      onClose();
      showNotification({
        title: 'Operácia prebehla úspešne',
        copy: '',
        type: 'SUCCESS',
      });
    } catch (error) {
      showNotification({
        title: 'Niečo sa pokazilo',
        copy: '',
        type: 'ERROR',
      });
    } finally {
      setLoading(false);
    }
  }, [client, mode]);

  return (
    <Modal
      visible={visible}
      onClose={onClose}
      destroyOnClose
      footer={
        <Modal.Actions
          actions={[
            { key: 'cancel', label: 'Zrušiť', variant: 'ghost', onClick: onClose },
            {
              key: 'submit',
              label: mode === 'paired' ? 'Prijať' : 'Odmietnuť',
              variant: mode === 'paired' ? 'primary' : 'danger',
              onClick: handlePairing,
              loading,
            },
          ]}
        />
      }
      title={title}
    >
      <Modal.Title>{`${title}: ${client?.profile.firstName} ${client?.profile.lastName}`}</Modal.Title>
      <Modal.Description>
        {mode === 'paired'
          ? 'Naozaj chcete prijať pacienta pod poskytovateľa?'
          : 'Naozaj chcete odmietnuť pacienta?'}
      </Modal.Description>
    </Modal>
  );
};

export default React.memo(PairingModal);
